import { useMemo } from 'react';

const DOTS = '...';

const range = (start: number, end: number) => {
  const length = end - start + 1;
  return Array(length)
    .fill(0)
    .map((_, index) => start + index);
};

interface Props {
  count: number;
  pageSize: number;
  currentPage: number;
  siblingCount?: number;
}

// Based on https://www.freecodecamp.org/news/build-a-custom-pagination-component-in-react/

export const usePagination = ({
  count,
  pageSize,
  currentPage,
  siblingCount = 1,
}: Props): Array<number | typeof DOTS> => {
  return useMemo(() => {
    const totalPageCount = Math.ceil(count / pageSize);

    if (totalPageCount <= 1) return [];

    const leftSiblingIndex = Math.max(currentPage - siblingCount, 1);
    const rightSiblingIndex = Math.min(currentPage + siblingCount, totalPageCount);

    const shouldShowLeftDots = leftSiblingIndex > 3;
    const shouldShowRightDots = rightSiblingIndex < totalPageCount - 2;

    if (totalPageCount <= 7) {
      return range(1, totalPageCount);
    }

    if (!shouldShowLeftDots && shouldShowRightDots) {
      const leftRange = range(1, 4);
      return [...leftRange, DOTS, totalPageCount];
    }

    if (shouldShowLeftDots && !shouldShowRightDots) {
      const rightRange = range(totalPageCount - 3, totalPageCount);
      return [1, DOTS, ...rightRange];
    }

    if (shouldShowLeftDots && shouldShowRightDots) {
      const middleRange = range(leftSiblingIndex, rightSiblingIndex);
      return [1, DOTS, ...middleRange, DOTS, totalPageCount];
    }

    return range(1, totalPageCount);
  }, [pageSize, currentPage, count, siblingCount]);
};
